import React from "react";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import Swal from "sweetalert2";
import { useAuth } from "../AuthProvider";
import APIService from "../../Service/APIService";
import TitleBar from "../../common/title-bar/title-bar";
import Button from "../../common/button/button";
import DropdownComponent from "../../common/dropdown-input";
import StatusIndicatorHeader from "../../common/status-indicator/status-indicator";
import Text from "../../common/typography/text";
import PermissionEditor from "./PermissionEditor";
import TabFade from "../../common/tab-fade";
import "./UserPermissions.css";

const TABS = ["AS Users", "Distributor Users", "Account Users"];

const TAB_APPLIES_TO = {
  "AS Users": "Admin",
  "Distributor Users": "Distributor",
  "Account Users": "Account",
};

const toOption = (value, label) => ({ value, label });

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const UserPermissions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const apiServiceRef = React.useRef(new APIService());

  const [selectedTab, setSelectedTab] = React.useState("AS Users");

  const [registry, setRegistry] = React.useState([]);

  const [asUsers, setAsUsers] = React.useState([]);
  const [distributors, setDistributors] = React.useState([]);
  const [distributorUsers, setDistributorUsers] = React.useState([]);
  const [accounts, setAccounts] = React.useState([]);
  const [accountUsers, setAccountUsers] = React.useState([]);

  const [selectedDistributor, setSelectedDistributor] = React.useState(null);
  const [selectedAccount, setSelectedAccount] = React.useState(null);
  const [selectedUser, setSelectedUser] = React.useState(null);

  const [resolvedPermissions, setResolvedPermissions] = React.useState({});
  const [pendingChanges, setPendingChanges] = React.useState({});

  const [isLoadingUsers, setIsLoadingUsers] = React.useState(false);
  const [isLoadingPermissions, setIsLoadingPermissions] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isUpdatingInvite, setIsUpdatingInvite] = React.useState(false);

  // Superadmin route guard — same pattern as every AdminTools screen.
  React.useEffect(() => {
    if (!user) return;
    if (user.Type !== "Admin" || !user.IsSuperAdmin) {
      navigate("/");
    }
  }, [navigate, user]);

  React.useEffect(() => {
    const fetchRegistry = async () => {
      try {
        const response = await apiServiceRef.current.getDataFromBackend(
          "getpermissiondefinitions"
        );
        const defs = response?.data?.data || {};
        setRegistry(
          Object.entries(defs).map(([key, def]) => ({ key, ...def }))
        );
      } catch (error) {
        Swal.fire(
          "Error",
          getErrorMessage(error, "Failed to load permission definitions"),
          "error"
        );
      }
    };
    fetchRegistry();
  }, []);

  React.useEffect(() => {
    const fetchTopLevelLists = async () => {
      setIsLoadingUsers(true);
      try {
        const [asUsersResponse, distributorsResponse, accountsResponse] =
          await Promise.all([
            apiServiceRef.current.getDataFromBackend("getasusers"),
            apiServiceRef.current.getDataFromBackend("getdistributors"),
            apiServiceRef.current.getDataFromBackend("getaccounts"),
          ]);
        setAsUsers(asUsersResponse?.data?.data || []);
        setDistributors(distributorsResponse?.data?.data || []);
        setAccounts(accountsResponse?.data?.data || []);
      } catch (error) {
        Swal.fire(
          "Error",
          getErrorMessage(error, "Failed to load users"),
          "error"
        );
      } finally {
        setIsLoadingUsers(false);
      }
    };
    fetchTopLevelLists();
  }, []);

  const resetSelection = () => {
    setSelectedUser(null);
    setResolvedPermissions({});
    setPendingChanges({});
  };

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setSelectedDistributor(null);
    setSelectedAccount(null);
    setDistributorUsers([]);
    setAccountUsers([]);
    resetSelection();
  };

  const handleSelectDistributor = async (option) => {
    setSelectedDistributor(option);
    setDistributorUsers([]);
    resetSelection();
    if (!option) return;
    setIsLoadingUsers(true);
    try {
      const response = await apiServiceRef.current.getDataFromBackend(
        `getdistributorusersbydistributor?DistributorID=${option.value}`
      );
      setDistributorUsers(response?.data?.data || []);
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Failed to load distributor users"),
        "error"
      );
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleSelectAccount = async (option) => {
    setSelectedAccount(option);
    setAccountUsers([]);
    resetSelection();
    if (!option) return;
    setIsLoadingUsers(true);
    try {
      const response = await apiServiceRef.current.getDataFromBackend(
        `getaccountusersbyaccount?AccountID=${option.value}`
      );
      // Contact rows (no login access) don't carry a UserID — exclude them.
      const users = (response?.data?.data || []).filter((row) => row.UserID);
      setAccountUsers(users);
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Failed to load account users"),
        "error"
      );
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleSelectUser = async (record) => {
    setSelectedUser(record);
    setPendingChanges({});
    if (!record?.UserID) return;
    setIsLoadingPermissions(true);
    try {
      const response = await apiServiceRef.current.getDataFromBackend(
        `getuserpermissions?UserID=${record.UserID}`
      );
      setResolvedPermissions(response?.data?.data?.Permissions || {});
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Failed to load permissions"),
        "error"
      );
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  const handleToggle = (key, checked) => {
    setPendingChanges((prev) => ({ ...prev, [key]: checked }));
  };

  const isSelfLockout = Boolean(
    selectedUser?.UserID && user?.ID === selectedUser.UserID
  );

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const handleSave = async () => {
    if (!selectedUser?.UserID || !hasPendingChanges) return;
    setIsSaving(true);
    try {
      const changes = Object.entries(pendingChanges).map(
        ([PermissionKey, IsGranted]) => ({ PermissionKey, IsGranted })
      );
      const response = await apiServiceRef.current.updateDataToBackend(
        { UserID: selectedUser.UserID, Changes: changes },
        "updateuserpermissions"
      );
      setResolvedPermissions(response?.data?.data?.Permissions || {});
      setPendingChanges({});
      Swal.fire("Saved", "Permissions updated successfully.", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Failed to save permissions"),
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditClick = () => {
    if (!selectedUser?.UserID) return;
    if (selectedTab === "AS Users") {
      navigate(`/ASUserDetails/${selectedUser.UserID}`);
    } else if (selectedTab === "Distributor Users") {
      navigate(`/User/${selectedUser.UserID}`);
    }
  };

  const buildAccountUserPayload = () => ({
    AccountID: selectedUser?.AccountID,
    UserID: selectedUser?.UserID,
    ID: selectedUser?.ID,
    Email: selectedUser?.Email,
  });

  const refreshAccountUsers = async () => {
    if (!selectedAccount) return;
    const response = await apiServiceRef.current.getDataFromBackend(
      `getaccountusersbyaccount?AccountID=${selectedAccount.value}`
    );
    const users = (response?.data?.data || []).filter((row) => row.UserID);
    setAccountUsers(users);
    const refreshed = users.find((u) => u.UserID === selectedUser?.UserID);
    if (refreshed) setSelectedUser(refreshed);
  };

  const handleResendInvite = async () => {
    if (!selectedUser) return;
    setIsUpdatingInvite(true);
    try {
      const response = await apiServiceRef.current.updateDataToBackend(
        buildAccountUserPayload(),
        "inviteaccountuser"
      );
      if (response?.status === 200) {
        await refreshAccountUsers();
        Swal.fire("Invited", "Invite sent successfully.", "success");
      } else {
        Swal.fire("Error", "Failed to send invite.", "error");
      }
    } catch (error) {
      Swal.fire("Error", getErrorMessage(error, "Failed to send invite"), "error");
    } finally {
      setIsUpdatingInvite(false);
    }
  };

  const handleRevokeAccess = async () => {
    if (!selectedUser) return;
    const confirm = await Swal.fire({
      title: "Revoke access?",
      text: "This user will no longer be able to sign in.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, revoke it",
    });
    if (!confirm.isConfirmed) return;

    setIsUpdatingInvite(true);
    try {
      const response = await apiServiceRef.current.updateDataToBackend(
        buildAccountUserPayload(),
        "revokeaccountuseraccess"
      );
      if (response?.status === 200) {
        await refreshAccountUsers();
        Swal.fire("Revoked", "User access revoked successfully.", "success");
      } else {
        Swal.fire("Error", "Failed to revoke user access.", "error");
      }
    } catch (error) {
      Swal.fire("Error", getErrorMessage(error, "Failed to revoke user access"), "error");
    } finally {
      setIsUpdatingInvite(false);
    }
  };

  const userOptions = React.useMemo(
    () =>
      asUsers.map((u) =>
        toOption(u.UserID, `${u.FirstName || ""} ${u.LastName || ""}`.trim() || u.Email)
      ),
    [asUsers]
  );
  const distributorOptions = React.useMemo(
    () => distributors.map((d) => toOption(d.ID, d.DistributorName)),
    [distributors]
  );
  const distributorUserOptions = React.useMemo(
    () =>
      distributorUsers.map((u) =>
        toOption(u.UserID, `${u.FirstName || ""} ${u.LastName || ""}`.trim() || u.Email)
      ),
    [distributorUsers]
  );
  const accountOptions = React.useMemo(
    () => accounts.map((a) => toOption(a.ID, a.AccountName)),
    [accounts]
  );
  const accountUserOptions = React.useMemo(
    () =>
      accountUsers.map((u) =>
        toOption(u.UserID, `${u.FirstName || ""} ${u.LastName || ""}`.trim() || u.Email)
      ),
    [accountUsers]
  );

  const currentUserList =
    selectedTab === "AS Users"
      ? asUsers
      : selectedTab === "Distributor Users"
      ? distributorUsers
      : accountUsers;

  const filteredDefs = React.useMemo(
    () =>
      registry.filter((def) =>
        def.appliesTo?.includes(TAB_APPLIES_TO[selectedTab])
      ),
    [registry, selectedTab]
  );

  const effectiveValues = React.useMemo(() => {
    const merged = { ...resolvedPermissions, ...pendingChanges };
    return merged;
  }, [resolvedPermissions, pendingChanges]);

  const displayName = selectedUser
    ? `${selectedUser.FirstName || ""} ${selectedUser.LastName || ""}`.trim() ||
      selectedUser.Email
    : "";

  const inviteStatus = selectedUser?.InviteAccepted
    ? { statusType: "success", status: "Invite Accepted" }
    : selectedUser?.Invited
    ? { statusType: "warning", status: "Invite Pending" }
    : { statusType: "failure", status: "Not Invited" };

  return (
    <div className="user-permissions-wrapper">
      <TitleBar
        entityName="Admin Tools"
        entityNameLink="/AdminTools"
        label="User Permissions"
        filterBarComponents={
          <>
            {TABS.map((tab) => (
              <Button
                key={tab}
                variant="timeframe"
                isActive={selectedTab === tab}
                onClick={() => handleTabChange(tab)}
              >
                {tab}
              </Button>
            ))}
          </>
        }
      />

      <TabFade activeKey={selectedTab}>
      <div className="user-permissions-pickers">
        {selectedTab === "AS Users" && (
          <DropdownComponent
            label="Select User"
            isRequired
            isSearchable
            placeholder="Select a user"
            options={userOptions}
            value={selectedUser ? toOption(selectedUser.UserID, displayName) : null}
            onChange={(option) =>
              handleSelectUser(asUsers.find((u) => u.UserID === option.value))
            }
            parentClassname="user-permissions-picker"
          />
        )}

        {selectedTab === "Distributor Users" && (
          <>
            <DropdownComponent
              label="Select Distributor"
              isRequired
              isSearchable
              placeholder="Select a distributor"
              options={distributorOptions}
              value={selectedDistributor}
              onChange={handleSelectDistributor}
              parentClassname="user-permissions-picker"
            />
            <DropdownComponent
              label="Select User"
              isRequired
              isSearchable
              placeholder="Select a user"
              isDisabled={!selectedDistributor}
              options={distributorUserOptions}
              value={selectedUser ? toOption(selectedUser.UserID, displayName) : null}
              onChange={(option) =>
                handleSelectUser(
                  distributorUsers.find((u) => u.UserID === option.value)
                )
              }
              parentClassname="user-permissions-picker"
            />
          </>
        )}

        {selectedTab === "Account Users" && (
          <>
            <DropdownComponent
              label="Select Account"
              isRequired
              isSearchable
              placeholder="Select an account"
              options={accountOptions}
              value={selectedAccount}
              onChange={handleSelectAccount}
              parentClassname="user-permissions-picker"
            />
            <DropdownComponent
              label="Select User"
              isRequired
              isSearchable
              placeholder="Select a user"
              isDisabled={!selectedAccount}
              options={accountUserOptions}
              value={selectedUser ? toOption(selectedUser.UserID, displayName) : null}
              onChange={(option) =>
                handleSelectUser(accountUsers.find((u) => u.UserID === option.value))
              }
              parentClassname="user-permissions-picker"
            />
          </>
        )}
      </div>

      {selectedUser && (
        <div className="user-permissions-identity-card">
          <div className="user-permissions-identity-info">
            <Text variant="bodyText" className="user-permissions-identity-name">
              {displayName}
            </Text>
            {selectedTab === "Account Users" && (
              <StatusIndicatorHeader {...inviteStatus} />
            )}
          </div>
          <div className="user-permissions-identity-actions">
            {selectedTab === "Account Users" ? (
              <>
                <Button
                  variant="primary"
                  style={{ background: "#27a907" }}
                  disabled={isUpdatingInvite}
                  onClick={handleResendInvite}
                >
                  Resend Invite
                </Button>
                <Button
                  variant="primary"
                  style={{ background: "#ff0000" }}
                  disabled={isUpdatingInvite}
                  onClick={handleRevokeAccess}
                >
                  Revoke Access
                </Button>
              </>
            ) : (
              <Button variant="primary" onClick={handleEditClick}>
                Edit
              </Button>
            )}
          </div>
        </div>
      )}

      {isSelfLockout && (
        <div className="user-permissions-self-lockout-banner">
          You can't edit your own permissions. Ask another superadmin to make
          this change.
        </div>
      )}

      {selectedUser && (
        <Spin spinning={isLoadingPermissions}>
          <PermissionEditor
            defs={filteredDefs}
            values={effectiveValues}
            onToggle={handleToggle}
            disabled={isSelfLockout || isLoadingPermissions}
          />
        </Spin>
      )}

      {selectedUser && !isSelfLockout && (
        <div className="user-permissions-save-bar">
          <Button
            variant="primary"
            disabled={!hasPendingChanges || isSaving}
            onClick={handleSave}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      )}

      {!selectedUser && currentUserList.length === 0 && !isLoadingUsers && (
        <div className="user-permissions-empty-state">
          <Text variant="smallText" color="lightText">
            {selectedTab === "AS Users"
              ? "No AS users found."
              : "Select an entry above to load its users."}
          </Text>
        </div>
      )}
      </TabFade>
    </div>
  );
};

export default UserPermissions;
